import { supabase } from '../supabaseClient';

// Get current user
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// PROBLEMS
export const fetchProblems = async () => {
  const { data, error } = await supabase
    .from('problems')
    .select('*, solutions (*)')
    .order('created_at', { ascending: false });

  if (error) throw error;
  
  // Ensure solutions is always an array for all problems
  const problemsWithSolutions = (data || []).map(problem => ({
    ...problem,
    solutions: problem.solutions || []
  }));
  
  return problemsWithSolutions;
};

export const createProblem = async (problemData) => {
  // Get logged-in user
  const { data: { user } } = await supabase.auth.getUser();

  // Prepare data with user ID
  const newProblemData = {
    ...problemData,
    created_by: user.id,  // Auto-fill with logged-in user
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  // Insert - use destructuring and NO .single()!
  const { data, error } = await supabase
    .from('problems')
    .insert([newProblemData])
    .select();

  if (error) {
    console.error('Insert error:', error);
    throw error;
  }

  return data[0]; // Return first item from array
};

export const updateProblem = async (id, problemData) => {
  const updatedData = {
    ...problemData,
    updated_at: new Date().toISOString()
  };

  const { data, error } = await supabase
    .from('problems')
    .update(updatedData)
    .eq('id', id)
    .select();

  if (error) {
    console.error('Update error:', error);
    throw error;
  }

  return data[0]; // Return first item from array
};

export const deleteProblem = async (id) => {
  const { error } = await supabase
    .from('problems')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Delete error:', error);
    throw error;
  }

  // Return success indicator
  return { success: true };
};

// SOLUTIONS
// Helper function to create the delete function in Supabase
export const createDeleteFunction = async () => {
  try {
    console.log('🔧 Creating delete_solutions_for_problem function...');
    
    const functionSQL = `
      CREATE OR REPLACE FUNCTION delete_solutions_for_problem(p_problem_id UUID)
      RETURNS INTEGER
      LANGUAGE plpgsql
      SECURITY DEFINER
      AS $$
      DECLARE
          deleted_count INTEGER;
      BEGIN
          DELETE FROM solutions 
          WHERE problem_id = p_problem_id;
          
          GET DIAGNOSTICS deleted_count = ROW_COUNT;
          
          RETURN deleted_count;
      END;
      $$;
      
      GRANT EXECUTE ON FUNCTION delete_solutions_for_problem(UUID) TO authenticated;
    `;
    
    const { data, error } = await supabase.rpc('exec_sql', { sql: functionSQL });
    
    if (error) {
      console.error('❌ Error creating function:', error);
      throw error;
    }
    
    console.log('✅ Function created successfully');
    return data;
  } catch (error) {
    console.error('❌ Failed to create function:', error);
    console.log('ℹ️ Please run the SQL commands in debug-rls-policies.sql manually in Supabase SQL Editor');
    throw error;
  }
};

// Helper function to debug RLS policies
export const debugSolutionsRLS = async () => {
  try {
    console.log('🔍 Debugging RLS policies for solutions table...');
    
    // Try to get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) {
      console.error('❌ Error getting current user:', userError);
      return;
    }
    
    console.log('👤 Current user:', user?.id);
    
    // Try a simple select to test read permissions
    const { data: testSelect, error: selectError } = await supabase
      .from('solutions')
      .select('*')
      .limit(1);
    
    if (selectError) {
      console.error('❌ SELECT test failed:', selectError);
    } else {
      console.log('✅ SELECT test passed, found', testSelect?.length || 0, 'solutions');
    }
    
    // Try a test delete on non-existent record to see if delete is allowed
    const { data: testDelete, error: deleteError } = await supabase
      .from('solutions')
      .delete()
      .eq('problem_id', 'test-non-existent-id')
      .select();
    
    if (deleteError) {
      console.error('❌ DELETE test failed:', deleteError);
      if (deleteError.code === '42501') {
        console.error('🚨 RLS POLICY ISSUE: DELETE operations are blocked by Row Level Security');
      }
    } else {
      console.log('✅ DELETE test passed (no records to delete, but permission granted)');
    }
    
  } catch (error) {
    console.error('❌ RLS debug error:', error);
  }
};

// Create or update solutions for a problem
export const saveSolutions = async (problemId, solutions) => {
  console.log('💾 saveSolutions: Starting for problem:', problemId);
  console.log('💾 Solutions to save:', solutions);
  
  if (!problemId) {
    const error = new Error('Problem ID is required to save solutions');
    console.error('❌ saveSolutions: Missing problem ID');
    throw error;
  }

  // 1. **Delete Existing Solutions** (RE-ENABLE THIS STEP!)
  // --- THIS IS THE CRITICAL LINE ---
  console.log('🗑️ Deleting existing solutions for problem:', problemId);
  const { error: deleteError } = await supabase
    .from('solutions')
    .delete()
    .eq('problem_id', problemId);

  if (deleteError) {
    console.error("Failed to delete old solutions:", deleteError.message);
    console.error("Delete error details:", deleteError);
    // We must return here, otherwise we insert duplicates!
    throw new Error("Deletion failed. Cannot save solutions.");
  }

  console.log('✅ Successfully deleted existing solutions');

  // 2. Prepare new solutions for insertion
  if (!solutions || solutions.length === 0) {
    console.log(`No solutions to insert for problem ${problemId}. Clean slate saved.`);
    return { data: [], error: null };
  }

  const solutionsToInsert = solutions
    .filter(s => s && s.description && s.description.trim() !== '')
    .map(s => ({
      problem_id: problemId,
      description: s.description.trim(),
      is_chosen: s.is_chosen || false
      // Note: We exclude the 'id' field to ensure Supabase generates fresh IDs
    }));

  if (solutionsToInsert.length === 0) {
    console.log(`No valid solutions to insert for problem ${problemId}. Clean slate saved.`);
    return { data: [], error: null };
  }

  console.log('💾 Inserting', solutionsToInsert.length, 'solutions');

  // 3. Insert the new/updated solutions list
  const { data: insertedSolutions, error: insertError } = await supabase
    .from('solutions')
    .insert(solutionsToInsert)
    .select('*');

  if (insertError) {
    console.error("Failed to insert new solutions:", insertError.message);
    console.error("Insert error details:", insertError);
    throw new Error("Insertion failed. Solution update incomplete.");
  }

  console.log('✅ Successfully inserted', insertedSolutions?.length || 0, 'solutions');
  return { data: insertedSolutions, error: null };
};

// Fetch solutions for a problem
export const fetchSolutions = async (problemId) => {
  const { data, error } = await supabase
    .from('solutions')
    .select('*')
    .eq('problem_id', problemId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Fetch solutions error:', error);
    throw error;
  }

  return data || [];
};

// DOCUMENTS
export const fetchDocuments = async () => {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const uploadDocument = async (file, metadata) => {
  console.log('📤 uploadDocument: Starting upload for file:', file.name);
  console.log('📤 uploadDocument: File size:', file.size, 'bytes');
  console.log('📤 uploadDocument: File type:', file.type);
  console.log('📤 uploadDocument: Metadata:', metadata);

  try {
    // Get logged-in user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error('❌ Error getting user:', userError);
      throw new Error('Authentication required for file upload');
    }

    if (!user) {
      throw new Error('User not authenticated');
    }

    console.log('👤 uploadDocument: User ID:', user.id);

    // Define filePath using user ID and a unique timestamp/filename combination
    const sanitizedFileName = file.name.replace(/\s/g, '_');
    const filePath = `${user.id}/${Date.now()}_${sanitizedFileName}`;
    
    console.log('📁 uploadDocument: File path:', filePath);

    // UPLOAD LOGIC: Ensure the logic relies ONLY on the destructured response
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('documents')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      // Handle error and RETURN immediately. Avoid manual response parsing.
      console.error("Storage Error:", uploadError.message);
      console.error("Storage Error Details:", uploadError);
      return { success: false, error: uploadError.message };
    }

    console.log('✅ uploadDocument: File uploaded successfully:', uploadData);

    // Get public URL (this should not cause response body issues)
    const { data: urlData } = supabase.storage
      .from('documents')
      .getPublicUrl(filePath);

    const publicUrl = urlData.publicUrl;
    console.log('🔗 uploadDocument: Public URL generated:', publicUrl);

    // METADATA INSERTION: Insert the document record into the 'documents' PostgreSQL table
    const documentRecord = {
      filename: file.name,
      file_path: filePath,
      file_type: file.type,
      file_size: file.size,
      file_url: publicUrl,
      category: metadata.category,
      description: metadata.description || '',
      uploaded_by: user.id,
      created_at: new Date().toISOString()
    };

    console.log('💾 uploadDocument: Inserting document record:', documentRecord);

    const { data: insertedData, error: insertError } = await supabase
      .from('documents')
      .insert([documentRecord])
      .select();

    if (insertError) {
      console.error("Failed to insert document metadata:", insertError.message);
      console.error("Insert error details:", insertError);
      
      // Clean up the uploaded file since metadata insertion failed
      try {
        await supabase.storage.from('documents').remove([filePath]);
        console.log('🧹 uploadDocument: Cleaned up uploaded file after metadata failure');
      } catch (cleanupError) {
        console.error('❌ Failed to cleanup file after metadata error:', cleanupError);
      }
      
      return { success: false, error: insertError.message };
    }

    console.log('✅ uploadDocument: Document metadata inserted successfully:', insertedData);

    // FINAL CODE: Return a simple success/error object
    return { 
      success: true, 
      data: insertedData[0],
      error: null 
    };

  } catch (error) {
    console.error('❌ uploadDocument: Unexpected error:', error);
    return { 
      success: false, 
      error: error.message || 'Upload failed' 
    };
  }
};

export const downloadDocument = async (filePath, filename) => {
  const { data, error } = await supabase.storage
    .from('documents')
    .download(filePath);

  if (error) throw error;

  // Create download link
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const deleteDocument = async (id, filePath) => {
  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from('documents')
    .remove([filePath]);

  if (storageError) {
    console.error('Storage delete error:', storageError);
    throw storageError;
  }

  // Delete from database
  const { error } = await supabase
    .from('documents')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Delete error:', error);
    throw error;
  }

  // Return success indicator
  return { success: true };
};
